import type {
  AdminPayoutDetailDto,
  AdminPayoutStatus,
  ListAdminPayoutsResultDto,
} from '@carcheck/shared';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { apiClient } from './client';

/**
 * TanStack Query hooks for the admin payouts surface (AUT-128 / ADMIN-16),
 * targeting the AUT-117 backend:
 *
 *   - `GET  /admin/payouts`                    (cursor-paginated)
 *   - `GET  /admin/payouts/:id`
 *   - `POST /admin/payouts/:id/retry`          (empty body)
 *   - `POST /admin/payouts/:id/mark-manual`    (`{note}`)
 *
 * Query-key convention (ADMIN-10 guardrail / queryClient.ts):
 *   list   → `['admin', 'payouts', 'list', filters]`
 *   detail → `['admin', 'payouts', 'detail', id]`
 * Both mutations invalidate by the `['admin', 'payouts']` prefix on success.
 *
 * Mirrors `admin-orders.ts`, with deliberate divergences (architect
 * guardrails — NON-NEGOTIABLE):
 *
 * 🟠 #7 INVALIDATE-ONLY mutations. Retry AND mark-manual both return the
 *      LIST-shape `{ dto: AdminPayoutListItemDto, previousStatus }` envelope
 *      — NOT the detail shape the open drawer reads. The SPA does NOT
 *      cross-patch the detail cache from the response; it just invalidates
 *      the prefix and lets TanStack refetch. Mirrors `useForceCancel`
 *      (orders), NOT `useApproveKyc` (inspectors).
 *
 * 🟠 #4 Backend is the SOLE eligibility authority. The list `retryEligible`
 *      / `markManualEligible` flags can go stale between fetch and click;
 *      a retry rejection arrives as HTTP 422 (codes mapped in
 *      `useRetryPayout`) and mark-manual's 409 is auto-toasted by the
 *      shared interceptor — on ANY rejection the caller `invalidate`s to
 *      refresh the row state.
 *
 * Server state lives in TanStack; the auth/session permission set lives in
 * MobX. The two never duplicate a datum.
 */

const PAYOUTS_BASE = '/admin/payouts';

/** Stable query-key prefix; both mutations invalidate against this. */
export const ADMIN_PAYOUTS_KEY = ['admin', 'payouts'] as const;

/**
 * Filter / pagination params for the list query. Mirrors the backend
 * `ListAdminPayoutsQuerySchema`:
 *   - `status` — multi-select over `ADMIN_PAYOUT_STATUSES` (CSV on the wire);
 *   - `inspectorId` — exact-match UUID;
 *   - `dateFrom` / `dateTo` — ISO date strings bounding `createdAt`;
 *   - `cursor` / `limit` — cursor pagination over `createdAt DESC`.
 * NO `q` free-text and NO `rail` filter — neither is in the query schema
 * (architect guardrail #2).
 */
export interface AdminPayoutsListParams {
  status?: AdminPayoutStatus[];
  inspectorId?: string;
  dateFrom?: string;
  dateTo?: string;
  cursor: string | null;
  limit: number;
}

/** Build the URLSearchParams a list fetch sends (omits empty values). */
const buildListParams = (params: AdminPayoutsListParams): Record<string, string> => {
  const query: Record<string, string> = { limit: String(params.limit) };
  if (params.status && params.status.length > 0) query.status = params.status.join(',');
  if (params.inspectorId && params.inspectorId.length > 0) query.inspectorId = params.inspectorId;
  if (params.dateFrom) query.dateFrom = params.dateFrom;
  if (params.dateTo) query.dateTo = params.dateTo;
  if (params.cursor) query.cursor = params.cursor;
  return query;
};

export const useAdminPayoutsList = (
  params: AdminPayoutsListParams,
): UseQueryResult<ListAdminPayoutsResultDto, Error> =>
  useQuery({
    queryKey: [...ADMIN_PAYOUTS_KEY, 'list', params],
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<ListAdminPayoutsResultDto>(PAYOUTS_BASE, {
        params: buildListParams(params),
        signal,
      });
      return data;
    },
  });

export const useAdminPayoutDetail = (
  id: string | null,
): UseQueryResult<AdminPayoutDetailDto, Error> =>
  useQuery({
    queryKey: [...ADMIN_PAYOUTS_KEY, 'detail', id],
    enabled: id !== null,
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<AdminPayoutDetailDto>(`${PAYOUTS_BASE}/${id}`, {
        signal,
      });
      return data;
    },
  });

/**
 * Low-level retry mutation. Empty request body — the server runs the
 * "cancel + replace" seam (flips the failed row to `superseded`, mints a
 * fresh `pending` row + OCT WebPay call). Role + permission + audit gated
 * server-side (AUT-138 removed per-action TOTP step-up).
 *
 * Returns nothing useful to the SPA — the response is the LIST-shape
 * `{dto, previousStatus}` envelope the audit interceptor reads, NOT the
 * detail shape (architect 🟠 #7); the caller invalidates rather than
 * cross-patching.
 */
export const postRetryPayout = async (id: string): Promise<void> => {
  await apiClient.post<unknown>(`${PAYOUTS_BASE}/${id}/retry`, {});
};

export interface MarkManualVariables {
  id: string;
  /** Required free-text justification (1-500 chars, threaded to the audit row). */
  note: string;
}

/**
 * Low-level mark-manual mutation. `note` is required (1-500 chars,
 * enforced by the dialog Zod schema + backend `MarkPayoutManualSchema`).
 * Flips `pending|failed → completed` and populates the manual-settlement
 * audit triple. Role + permission + audit gated server-side (AUT-138
 * removed per-action TOTP step-up).
 *
 * Returns the LIST-shape `{dto, previousStatus}` envelope (architect 🟠
 * #7) — the caller invalidates rather than cross-patching the detail.
 */
export const postMarkManual = async (variables: MarkManualVariables): Promise<void> => {
  await apiClient.post<unknown>(`${PAYOUTS_BASE}/${variables.id}/mark-manual`, {
    note: variables.note,
  });
};

/**
 * Cache-helper for the payout action hooks. ONLY exposes `invalidate`
 * (architect 🟠 #7 — no `setQueryData`): retry + mark-manual both return
 * the LIST-shape `{dto, previousStatus}` envelope, NOT
 * `AdminPayoutDetailDto`, so a cross-patch would mismatch the open
 * drawer's shape. Refetch via invalidation is the source of truth — and
 * it also re-syncs the possibly-stale `retryEligible` / `markManualEligible`
 * flags after any rejection.
 */
export const useAdminPayoutsCache = (): {
  invalidate: () => Promise<void>;
} => {
  const queryClient = useQueryClient();
  return {
    invalidate: () => queryClient.invalidateQueries({ queryKey: ADMIN_PAYOUTS_KEY }),
  };
};
